import { useRef, useState, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { impactFeedback, successFeedback } from '@/lib/native';

/**
 * Pull-to-refresh, the gesture every phone user already knows.
 *
 * Three details separate a real one from a web imitation, and all three are here:
 *  - It only arms at scroll top. Arming mid-list turns every upward flick into a fight.
 *  - Resistance. The indicator moves at a fraction of the finger, so the pull has weight instead of
 *    snapping to wherever the thumb went.
 *  - It commits at the threshold with a haptic, before the finger lifts — that tick is how people
 *    know the gesture took without watching the screen.
 */
export function PullToRefresh({
	onRefresh,
	children,
}: {
	onRefresh: () => Promise<void> | void;
	children: ReactNode;
}) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const startY = useRef(0);
	const armed = useRef(false);
	const committed = useRef(false);
	const [pull, setPull] = useState(0);
	const [refreshing, setRefreshing] = useState(false);

	const THRESHOLD = 68;

	const onTouchStart = (e: React.TouchEvent) => {
		const el = scrollRef.current;
		if (!el || refreshing) return;
		armed.current = el.scrollTop <= 0;
		committed.current = false;
		startY.current = e.touches[0]?.clientY ?? 0;
	};

	const onTouchMove = (e: React.TouchEvent) => {
		if (!armed.current || refreshing) return;
		const delta = (e.touches[0]?.clientY ?? 0) - startY.current;
		if (delta <= 0) {
			setPull(0);
			return;
		}
		// Resistance curve: the further you pull the less it gives, like a real rubber band.
		const resisted = Math.min(110, delta * 0.55);
		setPull(resisted);

		if (!committed.current && resisted >= THRESHOLD) {
			committed.current = true;
			void impactFeedback();
		}
	};

	const onTouchEnd = async () => {
		if (!armed.current || refreshing) return;
		armed.current = false;

		if (pull >= THRESHOLD) {
			setRefreshing(true);
			setPull(THRESHOLD);
			try {
				await onRefresh();
				void successFeedback();
			} finally {
				setRefreshing(false);
				setPull(0);
			}
			return;
		}
		setPull(0);
	};

	const active = pull > 0 || refreshing;

	return (
		<div
			ref={scrollRef}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onTouchEnd={onTouchEnd}
			className="h-full overflow-y-auto overscroll-y-contain"
		>
			<div
				className="flex items-center justify-center overflow-hidden"
				style={{
					height: pull,
					transition: active && !refreshing ? 'none' : 'height 260ms cubic-bezier(0.2, 0.8, 0.2, 1)',
				}}
			>
				<RefreshCw
					className="size-5"
					style={{
						color: pull >= THRESHOLD || refreshing ? 'var(--brand-primary)' : 'var(--brand-muted)',
						opacity: Math.min(1, pull / THRESHOLD),
						transform: `rotate(${refreshing ? 0 : pull * 3}deg)`,
						animation: refreshing ? 'ptrSpin 800ms linear infinite' : undefined,
					}}
				/>
			</div>

			{children}

			<style>{`
				@keyframes ptrSpin { to { transform: rotate(360deg); } }
				@media (prefers-reduced-motion: reduce) {
					@keyframes ptrSpin { from, to { transform: none; } }
				}
			`}</style>
		</div>
	);
}
